<template>
  <UserWrapper
    :errorMessage="errorMessage"
    :headerText="headerText"
    :mfaType="mfaType"
    :shouldHideEmailBadge="true"
  >
    <template
      #handler
    >
      <MfaView
        v-if="isMfa"
        @resetErrorMessage="onResetErrorMessage"
        :factors="factors"
        :mfaType="mfaType"
      >
        <component
          @error="onMfaError"
          :is="componentName"
          v-bind="componentProps"
          v-on="componentHandlers"
        />
      </MfaView>
      <component
        v-else
        @error="onCredentialError"
        :is="componentName"
        v-bind="componentProps"
        v-on="componentHandlers"
      />
    </template>
  </UserWrapper>
</template>

<script>
// Components
import DuoUserResetPasswordHandler from 'client/containers/user/UserResetPassword/UserResetPasswordMfaHandlers/DuoUserResetPasswordHandler.vue';
import LogoIcon from 'client/components/LogoIcon.vue';
import MfaView from 'client/containers/user/Views/MfaView.vue';
import PushUserResetPasswordHandler from 'client/containers/user/UserResetPassword/UserResetPasswordMfaHandlers/PushUserResetPasswordHandler.vue';
import TotpUserResetPasswordHandler from 'client/containers/user/UserResetPassword/UserResetPasswordMfaHandlers/TotpUserResetPasswordHandler.vue';
import UserResetPasswordInputHandler from 'client/containers/user/UserResetPassword/UserResetPasswordInputHandler.vue';
import UserWrapper from 'client/containers/user/Views/UserWrapper.vue';
import WebAuthnUserResetPasswordHandler from 'client/containers/user/UserResetPassword/UserResetPasswordMfaHandlers/WebAuthnUserResetPasswordHandler.vue';

// Constants
import { LoginConsts } from 'client/util/Constants';

// Service Errors
import MfaRequiredError from 'client/services/response/MfaRequiredError';
import RequestError from 'client/services/response/RequestError';

// Utils
import { useMfaLabels } from 'client/util/Constants/MfaLabels';
import ProcessPasswordResetMfaError from 'client/containers/user/MfaComponents/mfaUtils/processPasswordResetMfaError';
import getCurrentComponentNameFromList from 'client/containers/user/MfaComponents/mfaUtils/getCurrentComponentNameFromList';
import getLastUsedFactor from 'client/containers/user/MfaComponents/mfaUtils/getLastUsedFactor';
import mfaFactorBuilder from 'client/containers/user/MfaComponents/mfaUtils/mfaFactorBuilder';
import routes from 'client/routes/RouteGenerator';

const { login } = routes;
const {
  responseMessages: {
    mfaUnavailableForPwdReset,
    passwordResetError,
    passwordResetSuccess,
  },
} = LoginConsts;

const componentsUnderControl = [
  {
    id: '',
    componentName: UserResetPasswordInputHandler.name,
  },
  {
    id: 'duo',
    componentName: DuoUserResetPasswordHandler.name,
  },
  {
    id: 'push',
    componentName: PushUserResetPasswordHandler.name,
  },
  {
    id: 'totp',
    componentName: TotpUserResetPasswordHandler.name,
  },
  {
    id: 'webauthn',
    componentName: WebAuthnUserResetPasswordHandler.name,
  },
];

const getCurrentComponentName = getCurrentComponentNameFromList(componentsUnderControl);

const formInitialState = {
  password: '',
  confirm: '',
};

export default {
  name: 'UserResetPasswordController',

  components: {
    DuoUserResetPasswordHandler,
    LogoIcon,
    MfaView,
    PushUserResetPasswordHandler,
    TotpUserResetPasswordHandler,
    UserResetPasswordInputHandler,
    UserWrapper,
    WebAuthnUserResetPasswordHandler,
  },

  props: {
    mfaType: {
      type: String,
      default: '',
    },
    requestKey: {
      type: String,
      default: '',
    },
    step: {
      type: String,
      default: '',
    },
  },

  setup() {
    const mfaLabels = useMfaLabels();

    return { mfaLabels };
  },

  data() {
    return {
      errorMessage: '',
      factors: [],
      form: {
        ...formInitialState,
      },
    };
  },

  computed: {
    componentHandlers() {
      return {
        // InputHandler, Duo, Push, Totp, WebAuthn
        resetErrorMessage: this.onResetErrorMessage,
        // InputHandler, Duo, Push, Totp, WebAuthn
        success: this.onSuccess,
      };
    },

    componentName() {
      const id = this.isMfa ? this.mfaType : this.step;

      return getCurrentComponentName(id);
    },

    componentProps() {
      return {
        // InputHandler, Duo, Push, Totp, WebAuthn
        form: this.form,
        // InputHandler, Duo, Push, Totp, WebAuthn
        requestKey: this.requestKey,
      };
    },

    controllingQueryParams() {
      return {
        template: 'resetPasswordUserForm',
        key: this.requestKey,
      };
    },

    headerText() {
      return this.isMfa ? this.mfaLabels.verifyIdentity : this.mfaLabels.passwordResetHeader;
    },

    isMfa() {
      return this.step === 'mfa';
    },
  },

  mounted() {
    this.validateInitialStep();
  },

  methods: {
    handleResetForm() {
      this.form = {
        ...formInitialState,
      };
    },

    onCredentialError(error) {
      // todo: update error message to respond to action
      if (error instanceof RequestError) {
        return this.setErrorMessage(error.message);
      }
      if (!(error instanceof MfaRequiredError)) {
        return this.setErrorMessage(passwordResetError);
      }

      this.factors = mfaFactorBuilder(
        {
          factors: error.factors,
        },
        {
          controllingQueryParams: this.controllingQueryParams,
        },
      );

      if (this.factors.length === 0) {
        return this.setErrorMessage(mfaUnavailableForPwdReset);
      }

      this.form.challenge = error.challenge;
      this.form.pwdResetToken = error.pwdResetToken;

      const mfaParam = getLastUsedFactor(this.factors);

      return this.$router.push({
        path: login,
        query: {
          ...this.controllingQueryParams,
          step: 'mfa',
          ...mfaParam,
        },
      });
    },

    onMfaError(error) {
      const { message, action } = ProcessPasswordResetMfaError(this.mfaType, error);
      if (action === 'exitToLogin') {
        this.handleResetForm();
        this.redirectToLogin(message);
      } else if (action === 'restart') {
        this.handleResetForm();
        this.redirectBackToResetStart(message);
      } else {
        this.setErrorMessage(message);
      }
    },

    onResetErrorMessage() {
      this.setErrorMessage();
    },

    onSuccess() {
      this.$router.push({ name: 'Login', params: { success: passwordResetSuccess } });
    },

    // TODO: the redirect methods should move to the router service when it's created
    redirectBackToResetStart(message) {
      if (message) {
        this.setErrorMessage(message);
      }
      this.$router.push({
        path: login,
        query: {
          template: 'resetPasswordUserForm',
          key: this.requestKey,
        },
      });
    },
    redirectToLogin(errorMessage) {
      this.$router.push({
        name: 'Login',
        params: { error: errorMessage },
      });
    },

    setErrorMessage(message = '') {
      this.errorMessage = message;
    },

    validateInitialStep() {
      const isInitialStepInvalid = this.step && !this.factors.length;

      if (isInitialStepInvalid) {
        this.$router.replace({ path: login, query: { ...this.controllingQueryParams } });
      }
    },
  },
};
</script>

<style module>
@import '../../../css/login-components.css';

.duoContainer {
  max-width: fit-content;
}

/* Override breakpoint for .logoIconFixed from LogoIcon to account for wider card */
@media (max-width: 1058px) {
  .duoLogoPosition {
    bottom: 0;
    display: block;
    margin: 3rem 0 2rem auto;
    position: relative;
    right: 0;
  }
}
</style>
